import React from "react";
import BaseImage from "../../components/atoms/image/BaseImage";
import jacobme from "../../assets/jacobme.jpg";
import Navbar from "../../components/molecules/Navbar/Navbar";
import gee1 from "../../assets/portfolio/gee1.png";
import richnessposter from "../../assets/portfolio/richnessposter.jpeg";
import cricketposter from "../../assets/portfolio/cricketposter.jpeg";

const About = () => {
  return (
    <div className="min-h-screen bg-cloud-100">
      <div className="pt-4 px-4">
        <Navbar variant="light" />
      </div>

      {/* Header */}
      <div className="text-center pt-8 pb-12">
        <h1 className="text-4xl font-lato font-semibold text-pine-800">
          About Me
        </h1>
      </div>

      {/* Profile image */}
      <div className="mx-auto mb-8 w-40 h-40 rounded-full overflow-hidden ring-4 ring-moss-300">
        <BaseImage src={jacobme} className="w-full h-full object-cover" />
      </div>

      {/* Bio section */}
      <div className="max-w-2xl mx-auto px-6 pb-16 space-y-6 text-earth-700 leading-relaxed">
        <p>
          I was born and raised in West Texas and was the first person in my
          family to attend university.
        </p>
        <p>
          In university, I was chosen as a McNair Scholar, a distinction awarded
          to students from low-income families. It changed the course of my life
          and I carry so much gratitude for it.
        </p>
        <p>
          It set me on the path to becoming an Environmental Researcher and
          Analyst. I got to work on genuinely meaningful problems, including
          research for the World Bank, and built strong skills in data
          analytics, research, and coding along the way.
        </p>
        <p>
          Over the years, I've held a wide range of roles, including Legislative
          Intern in the U.S. House of Representatives. I've also worked as
          restaurant staff, a non-profit intern, an after-school tutor, a
          website designer, an English teacher, a lab tech, and a front-end
          developer. Each experience has shaped how I think and work.
        </p>
        <p>
          Today, I bring all those experiences together while building and
          growing SkinSort.
        </p>
        <p>
          But more than the skills, what those years gave me was perspective.
          I've learned to stay curious, to lead with kindness and empathy, and
          to find meaning in the work itself rather than just the outcome.
        </p>
        <p>
          I'm not the same person who started this journey and am so grateful
          for every step that got me here.
        </p>
      </div>

      {/* Work section */}
      <div className="max-w-2xl mx-auto px-6 pb-20">
        <h2 className="text-3xl font-lato font-semibold text-pine-800 text-center mb-12">
          Pieces of my work
        </h2>

        <div className="space-y-12">
          {/* SkinSort */}
          <div>
            <h3 className="text-xl font-semibold text-pine-800 mb-3">
              SkinSort
            </h3>
            <p className="text-earth-700 leading-relaxed">
              <a
                href="https://skinsort.com/"
                rel="noreferrer"
                target="_blank"
                className="text-pine-700 hover:text-pine-900 underline underline-offset-2"
              >
                SkinSort
              </a>{" "}
              began as a napkin sketch during the pandemic. Today, it's used by
              millions of people around the world.
            </p>
          </div>

          {/* Deforestation */}
          <div>
            <h3 className="text-xl font-semibold text-pine-800 mb-3">
              Deforestation in Asia
            </h3>
            <p className="text-earth-700 leading-relaxed mb-4">
              I used to work as a Research Assistant and Analyst on
              Deforestation in the Asian continent. My duties included compiling
              an extensive literature review, working with raster data for
              forest gain and loss, analyzing the data, and visualizing it.
            </p>
            <BaseImage className="w-1/2 mx-auto my-6" src={gee1} />
            <p className="text-earth-700 leading-relaxed">
              This data was used for various reforesting projects in countries
              such as Cambodia, Vietnam, and Indonesia.
            </p>
          </div>

          {/* Biodiversity */}
          <div>
            <h3 className="text-xl font-semibold text-pine-800 mb-3">
              Biodiversity in South Texas Cave System
            </h3>
            <p className="text-earth-700 leading-relaxed mb-4">
              I have been lucky enough to have presented my research at St.
              Mary's University, The Texas Wildlife Conference, and UC Berkeley
              McNair Symposium.
            </p>
            <p className="text-earth-700 leading-relaxed">
              You can read my publication{" "}
              <a
                href="http://uis-speleo.org/wp-content/uploads/2020/08/Proceedings-17th-ICS-Vol1-Ed2-screen.pdf"
                rel="noreferrer"
                target="_blank"
                className="text-pine-700 hover:text-pine-900 underline underline-offset-2"
              >
                here
              </a>
              .
            </p>
            <div className="flex flex-col sm:flex-row gap-4 my-6">
              <BaseImage className="sm:w-1/2" src={richnessposter} />
              <BaseImage className="sm:w-1/2" src={cricketposter} />
            </div>
          </div>

          {/* Web Developer */}
          <div>
            <h3 className="text-xl font-semibold text-pine-800 mb-3">
              Web Developer Certificate
            </h3>
            <p className="text-earth-700 leading-relaxed">
              In 2021, I decided to enhance my development skills by attending
              coding bootcamp. Learning web development was difficult, but I
              persevered and earned my certificate!
            </p>
          </div>

          {/* Publications */}
          <div>
            <h3 className="text-xl font-semibold text-pine-800 mb-3">
              Publications
            </h3>
            <ul className="text-earth-700 leading-relaxed space-y-2">
              <li>
                • Seasonal Variations in Species Abundance and Impact in Robber
                Baron Cave, Texas (Page 117)
              </li>
              <li>
                • Does a Seasonal Distribution Pattern of the Cave Cricket,
                Ceuthophilus Cunicular, Exist Within the Robber Baron Cave
                System? - Hardcopy only
              </li>
            </ul>
          </div>

          {/* In Conversation */}
          <div>
            <h3 className="text-xl font-semibold text-pine-800 mb-3">
              In Conversation
            </h3>
            <ul className="text-earth-700 leading-relaxed space-y-2">
              <li>
                •{" "}
                <a
                  href="https://scienceandskincare.blog/2021/10/05/in-conversation-with-the-founders-of-skinsort/"
                  rel="noreferrer"
                  target="_blank"
                  className="text-pine-700 hover:text-pine-900 underline underline-offset-2"
                >
                  In conversation with the founders of SkinSort
                </a>
              </li>
              <li>
                •{" "}
                <a
                  href="https://www.goodrx.com/conditions/generalized-anxiety-disorder/mental-health-stigma-in-asian-communities"
                  rel="noreferrer"
                  target="_blank"
                  className="text-pine-700 hover:text-pine-900 underline underline-offset-2"
                >
                  Mind Over Stigma: How 3 Asian Americans Cope With Anxiety
                </a>{" "}
                by Rebecca Samuelson
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
